import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../api.js';
import QuestionTypeBlockEditor from '../components/QuestionTypeBlockEditor.js';
import {
  isQuestionBlockValid,
  newQuestionBlockDraft,
  toQuestionDraft,
} from '../lib/customQuestionDraft.js';

/**
 * Organizer-only: the screen the "+" card at the end of a custom
 * competition's question list links to (PLAN.md Phase 10.3/10.4). Reuses
 * the exact same block editor the creation screen uses for each of its
 * question blocks, just exactly one of them.
 */
export default function AddQuestionScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = '', poolId = '' } = useParams();

  const [block, setBlock] = useState(newQuestionBlockDraft());

  const add = useMutation({
    mutationFn: () => api.addQuestion(id, poolId, toQuestionDraft(block)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['questions', id, poolId] });
      void navigate(`/groups/${id}/bolaos/${poolId}/predictions`);
    },
  });

  const canSubmit = isQuestionBlockValid(block) && !add.isPending;

  return (
    <main className="screen">
      <Link to={`/groups/${id}/bolaos/${poolId}/predictions`} className="muted">
        {t('common.back')}
      </Link>
      <h1>{t('customCompetition.addQuestionTitle')}</h1>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          add.mutate();
        }}
      >
        <QuestionTypeBlockEditor block={block} onChange={setBlock} />

        {add.isError ? <p className="error">{t('common.somethingWentWrong')}</p> : null}

        <button type="submit" className="button button-primary" disabled={!canSubmit}>
          {add.isPending
            ? t('customCompetition.addingQuestion')
            : t('customCompetition.addQuestionSubmit')}
        </button>
      </form>
    </main>
  );
}
